import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import PageWrapper from "./talent/components/PageWrapper";

/* ---------------- AUTH ---------------- */
import Login from "./Login";
import SignUp from "./SignUp";
import ForgotPassword from "./ForgotPassword";

/* ---------------- TALENT PRIVATE ---------------- */
import TalentHome from "./TalentHome";
import NavigationTalent from "./talent/NavigationTalent";
import SubmissionHistory from "./SubmissionHistory";
import ViewNotifications from "./talent/ViewNotifications";
import NotificationPreferences from "./talent/NotificationPreferences";
import MessagingTalent from "./talent/messagingTalent";
import TalentOnboardingForm from "./TalentOnboardingForm";
import OnboardingRedirector from "./OnboardingRedirector";

/* ---------------- TALENT PUBLIC ---------------- */
import TalentPublicView from "./talent/TalentPublicView";
import TalentViewPage from "./TalentView";

/* ---------------- CASTING ---------------- */
import CastingNoticesPage from "./CastingNoticesPage";
import NoticeDetailPage from "./NoticeDetailPage";
import TalentSubmission from "./TalentSubmission";
import MediaSelectionDialog from "./MediaSelectionDialog";
import PostedNoticesPage from "./admin/PostedNoticesPage";

/* ---------------- CLIENT DASHBOARD ---------------- */
import Dashboard from "./Dashboard";
import PostCastingCall from "./PostCastingCall";
import RoleCreator from "./RoleCreator";
import SummaryDetails from "./SummaryDetails";
import RoleStatus from "./RoleStatus";
import Search from "./Search";
import Favourites from "./Favourites";
import Account from "./Account";
import Chat from "./messaging";

/* ---------------- ADMIN ---------------- */
import AdminDashboard from "./admin/AdminDashboard";
import NotificationList from "./admin/NotificationsList";
import UsersListPage from "./admin/UsersListPage";
import UserDetailsPage from "./admin/UserDetailsPage";
import ViewNotice from "./admin/components/ViewNotice";
import AddNewUserPage from "./admin/AddNewUserPage";
import AdminQA from "./admin/Adminqa";
import Broadcast from "./admin/Broadcast";

/* ---------------- PAGES ---------------- */
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";
import ProfilePage from "./talent/onboarding/ProfilePage";

/* ---------------- STATIC ---------------- */
import TermsOfServicePage from "./termsAndConditions";
import PrivacyPolicy from "./PrivacyPolicyPage";
import Contact from "./Contact";
import Notifications from "./talent/pages/Notifications";
import TalentIntro from "./landing/TalentIntro";
import DirectorIntro from "./landing/DirectorIntro";
import Questions from "./components/questions";
import IntroPage from "./landing/IntroPage";
import AboutDirectSubmit from "./landing/tourPages/AboutDirectSubmit";
import DirectorTour from "./landing/tourPages/DirectorTour";
import TalentTour from "./landing/tourPages/TalentTour";

import { UserProvider } from "./context/UserContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import ForgotUsername from "./components/ForgotUsername";
import { VerifiedDirectorProvider } from "./utils/VerifiedDirectorContext";
import BroadcastLogs from "./admin/BulkEmailHistory";

import "./styles/global.css";
import "normalize.css";

/* ---------------- THEME ---------------- */

const theme = createTheme({
  palette: {
    primary: {
      main: "#90b5fe",
      contrastText: "#fff",
    },
    success: {
      main: "#15d398",
      contrastText: "#fff",
    },
    error: {
      main: "#d17c7c",
      contrastText: "#fff",
    },
    portalGreen: {
      light: "#D7F5E3",
      main: "#15d398",
      dark: "#4FA377",
      contrastText: "#0f2e1f",
    },
  },

  typography: {
    fontFamily: "Source Sans 3, sans-serif",
    h5: { fontSize: "24px", fontWeight: 600 },
    body2: { fontSize: "15px", lineHeight: 1.4 },
    body1: { fontSize: "13px", lineHeight: 1.5 },
    caption: { fontSize: "12px", opacity: 0.85 },
  },

  components: {
    /* ================= BUTTON ================= */
    MuiButton: {
      defaultProps: {
        variant: "contained",
        color: "primary",
      },
      styleOverrides: {
        containedPrimary: {
          backgroundColor: "#90b5fe",
          "&:hover": { backgroundColor: "#7A99D8" },
        },
      },
      variants: [
        {
          props: { color: "portalGreen" },
          style: {
            backgroundColor: "#16a34a",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#15803d",
            },
          },
        },
      ],
    },

    /* ================= LINK ================= */
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          "&:hover": { textDecoration: "none" },
        },
      },
    },

    /* ================= SNACKBAR POSITION ================= */
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          disableScrollLock: true,
          disablePortal: true,
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiPopover: {
      defaultProps: {
        disablePortal: true,
      },
    },

    /* ================= ALERT (SNACKBAR COLORS) ================= */
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
        },
        standardSuccess: {
          backgroundColor: "#15d398",
          color: "#fff",
        },
        standardError: {
          backgroundColor: "#d17c7c",
          color: "#fff",
        },
        standardWarning: {
          backgroundColor: "#f59e0b",
          color: "#fff",
        },
        standardInfo: {
          backgroundColor: "#3b82f6",
          color: "#fff",
        },
      },
    },
  },
});

/* ---------------- FIX RESIZE OBSERVER WARNING ---------------- */

if (process.env.NODE_ENV === "development") {
  const resizeObserverErr = window.console.error;

  window.console.error = (...args) => {
    if (
      args[0]?.includes?.(
        "ResizeObserver loop completed with undelivered notifications",
      )
    ) {
      return;
    }
    resizeObserverErr(...args);
  };
}

/* ---------------- APP ---------------- */

function App() {
  const [userId, setUserId] = useState(null);

  const [showSessionDialog, setShowSessionDialog] = useState(false);
  useEffect(() => {
    setUserId(localStorage.getItem("userId"));
  }, []);



useEffect(() => {
  const handleStorageChange = (event) => {

    if (event.key === "logout") {
      setShowSessionDialog(true);
    }
  };

  window.addEventListener("storage", handleStorageChange);

  return () => {
    window.removeEventListener("storage", handleStorageChange);
  };
}, []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <UserProvider>
        <Router>
          <ScrollToTop />
<Routes>
  {/* AUTH */}
<Route path="/" element={<Navigate to="/talentintro" replace />} />

  <Route
    path="/login"
    element={
      <PageWrapper title="Login">
        <Login />
      </PageWrapper>
    }
  />
<Route path="/forgot-username" element={<ForgotUsername />} />
  <Route
    path="/signup"
    element={
      <PageWrapper title="Sign Up">
        <SignUp />
      </PageWrapper>
    }
  />

  <Route
    path="/forgot-password"
    element={
      <PageWrapper title="Forgot Password">
        <ForgotPassword />
      </PageWrapper>
    }
  />

  {/* ================= DIRECTOR / COMPANY ================= */}

  <Route
    path="/dashboard"
    element={
      <ProtectedRoute allowedRoles={["COMPANY"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Dashboard">
          <Dashboard />
          
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/post-casting-call"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Post Casting Call">
          <PostCastingCall />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/post-casting-call/:noticeId"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Edit Casting Call">
          <PostCastingCall />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/view-notice/:noticeId"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Notice Details">
          <ViewNotice />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/role-creator"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Role Creator">
          <RoleCreator />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/summarydetails"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Summary Details">
          <SummaryDetails />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/rolestatus"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Role Status">
          <RoleStatus />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/search"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Search">
          <Search />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/favourites"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Favourites">
          <Favourites />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/account"
    element={
      <ProtectedRoute allowedRoles={["COMPANY", "USER", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Account">
          <Account />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/inbox"
    element={
      <ProtectedRoute allowedRoles={["COMPANY"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Inbox">
          <Chat />
        </PageWrapper>
      </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  {/* ================= USER ================= */}

  <Route
    path="/myresume"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="My Resume">
          <TalentHome key={userId} userId={userId} />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/editresume"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Edit Resume">
          <TalentHome key={userId} userId={userId} />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/media-selection"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Select Media">
          <MediaSelectionDialog />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/talent-submission/:noticeId"
    element={
      <ProtectedRoute allowedRoles={["COMPANY"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Submit to Casting">
          <TalentSubmission />
        </PageWrapper>
        </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  <Route
    path="/castingcalls"
    element={
      <ProtectedRoute allowedRoles={["USER","ADMIN"]}>
        <PageWrapper title="Casting Calls">
          <CastingNoticesPage />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/notice/:noticeId"
    element={
      <ProtectedRoute allowedRoles={["USER", "ADMIN"]}>
        <PageWrapper title="Notice Details">
          <NoticeDetailPage />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/submission-history"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Submission History">
          <SubmissionHistory userId={userId} />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/view-notifications"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Notifications">
          <ViewNotifications />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/notifications-prefs"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Notification Preferences">
          <NotificationPreferences />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/messaging"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Messaging">
          <MessagingTalent />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/talent/:talentId"
    element={
      <ProtectedRoute allowedRoles={["USER", "COMPANY", "ADMIN"]}>
        <VerifiedDirectorProvider>
        <PageWrapper title="Talent Profile">
          <TalentViewPage />
        </PageWrapper>
        </VerifiedDirectorProvider>
      </ProtectedRoute>
    }
  />

  {/* ================= ONBOARDING ================= */}

  <Route
    path="/onboarding"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Onboarding">
          <OnboardingRedirector />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/onboarding/basic"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Basic Information">
          <TalentOnboardingForm />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/onboarding/resume"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Resume Setup">
          <TalentOnboardingForm />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/onboarding/media"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Media Upload">
          <TalentOnboardingForm />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  {/* ================= ADMIN ================= */}

  <Route
    path="/admin-dashboard"
    element={
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <PageWrapper title="Admin Dashboard">
          <AdminDashboard />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/notification-list"
    element={
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <PageWrapper title="Admin Notifications">
          <NotificationList />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/admin/user-list"
    element={
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <PageWrapper title="Users">
          <UsersListPage />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

<Route
    path="/admin/broadcast"
    element={
      <ProtectedRoute allowedRoles={["ADMIN"]}>   
      <PageWrapper title="Broadcast">
          <Broadcast />
          </PageWrapper>
          </ProtectedRoute>
    }
  />

  <Route
  path="/admin/bulk-email-history"
  element={
    <ProtectedRoute allowedRoles={["ADMIN"]}>
      <PageWrapper title="Bulk Email History">
        <BroadcastLogs />
      </PageWrapper>
    </ProtectedRoute>
  }
  />

  <Route
    path="/admin/user/:email"
    element={
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <PageWrapper title="User Details">
          <UserDetailsPage />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/admin/add-user"
    element={
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <PageWrapper title="Add User">
          <AddNewUserPage />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/admin/posted-notices/:email"
    element={
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <PageWrapper title="Posted Notices">
          <PostedNoticesPage />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/admin/questions"
    element={
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <PageWrapper title="Q&A Management">
          <AdminQA />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  {/* ================= PUBLIC ================= */}

  <Route
    path="/terms-and-condition"
    element={
      <PageWrapper title="Terms and Conditions">
        <TermsOfServicePage />
      </PageWrapper>
    }
  />

  <Route
    path="/privacy-policy"
    element={
      <PageWrapper title="Privacy Policy">
        <PrivacyPolicy />
      </PageWrapper>
    }
  />

  <Route
    path="/questions"
    element={
      <PageWrapper title="Question and Answers">
        <Questions />
      </PageWrapper>
    }
  />

  <Route
    path="/contact"
    element={
      <PageWrapper title="Contact">
        <Contact />
      </PageWrapper>
    }
  />

  <Route
    path="/payment-success"
    element={
      <PageWrapper title="Payment Success">
        <PaymentSuccess />
      </PageWrapper>
    }
  />

  <Route
    path="/payment-cancelled"
    element={
      <PageWrapper title="Payment Cancelled">
        <PaymentCancelled />
      </PageWrapper>
    }
  />

  <Route
    path="/profile"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Profile">
          <ProfilePage />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route
    path="/notifications"
    element={
      <ProtectedRoute allowedRoles={["USER"]}>
        <PageWrapper title="Notifications">
          <Notifications />
        </PageWrapper>
      </ProtectedRoute>
    }
  />

  <Route path="/talentintro" element={<TalentIntro />} />
  <Route path="/directorintro" element={<DirectorIntro />} />
  <Route path="/talent-tour" element={<TalentTour />} />
  <Route path="/director-tour" element={<DirectorTour />} />
  <Route
    path="/about-directsubmit"
    element={<AboutDirectSubmit />}
  />

  {/* PUBLIC PROFILE */}
  <Route
    path="/:username"
    element={
      <PageWrapper title="Talent Profile">
        <TalentPublicView />
      </PageWrapper>
    }
  />
</Routes>
        </Router>
      </UserProvider>
      <Dialog open={showSessionDialog}>
  <DialogTitle>
    Session Ended
  </DialogTitle>

  <DialogContent>
    <Typography>
      Your session was logged out in another tab.
      Please login again to continue.
    </Typography>
  </DialogContent>

  <DialogActions>
    <Button
      onClick={() => {
        window.location.href = "/login";
      }}
      variant="contained"
    >
      Login Again
    </Button>
  </DialogActions>
</Dialog>
    </ThemeProvider>
  );
}

export default App;
